import captureError from '@disco/utils/captureError'
import type { GroupId } from 'types/group'
import type { Playlist, PlaylistId, PlaylistTag } from 'types/playlist'
import type { GetState, Thunk } from 'types/redux'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import {
  loadBatch,
  mapResponseToReducerObjects,
} from '@disco/state-management/actions/PlaylistTrackActions'
import api from '@disco/api/server'
import {
  PLAYLIST_LOAD,
  PLAYLIST_LOAD_FAIL,
  PLAYLIST_LOAD_PARAMS,
  PLAYLIST_LOAD_PLAIN_UPDATE,
  PLAYLIST_LOAD_SUCCESS,
  PLAYLIST_LOAD_ZIP_URLS,
  PLAYLIST_LOAD_ZIP_URLS_FAIL,
  PLAYLIST_LOAD_ZIP_URLS_SUCCESS,
  PLAYLIST_UPDATE,
  PLAYLIST_UPDATE_FAIL,
  PLAYLIST_UPDATE_SUCCESS,
  PLAYLISTS_CURATED_LOAD,
  PLAYLISTS_CURATED_LOAD_FAIL,
  PLAYLISTS_CURATED_LOAD_SUCCESS,
  PLAYLISTS_LOAD_NEXT,
  PLAYLISTS_LOAD_NEXT_FAIL,
  PLAYLISTS_LOAD_NEXT_SUCCESS,
  PLAYLISTS_SEARCH,
  PLAYLISTS_SEARCH_FAIL,
  PLAYLISTS_SEARCH_SUCCESS,
  PLAYLISTS_UPDATE,
  PLAYLISTS_UPDATE_FAIL,
  PLAYLISTS_UPDATE_SUCCESS,
  WATERMARKING_UPDATE_ITEMS,
} from '@disco/constants/ActionTypes'
import { ALBUMS } from '@disco/constants/FeatureTypes'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { getPlaylist } from '@disco/state-management/selectors/playlists'
import { omit } from '@disco/utils/array'
import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
/**
 * Loads ZIP download URLs for a playlist.
 * @param playlist - Playlist object with optional zip URLs
 * @returns Thunk that loads playlist ZIP URLs and handles success/error states
 */
export const loadPlaylistZipUrls = (playlist: {
  playlist_zip_urls?: string
}): ((dispatch: (...args: Array<any>) => any) => Promise<any>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    try {
      dispatch({
        type: PLAYLIST_LOAD_ZIP_URLS,
        playlist,
      })
      const urls = await api.getPlaylistZipUrls(playlist)
      dispatch({
        type: PLAYLIST_LOAD_ZIP_URLS_SUCCESS,
        playlist,
        urls,
      })
      return urls
    } catch (error) {
      return dispatch({
        type: PLAYLIST_LOAD_ZIP_URLS_FAIL,
        playlist,
        error,
      })
    }
  }
}
/**
 * Loads tracks for a playlist with optional territory filtering.
 * @param playlist - Playlist object to load tracks for
 * @param territories - Optional array of territory group IDs for filtering
 * @returns Thunk that loads playlist tracks and maps them to reducer objects
 */
export function getPlaylistTracks(playlist: Playlist, territories?: GroupId[]) {
  return (dispatch, getState) => {
    return api
      .getFullPlaylist(playlist, territories)
      .then(result => {
        mapResponseToReducerObjects(dispatch, getState, result)
        return result
      })
      .catch(error => dispatch(sendGenericError(error)))
  }
}
/**
 * Loads playlist parameters and configuration.
 * @param playlist - Playlist object to load parameters for
 * @returns Thunk that loads playlist parameters
 */
export const getPlaylistParams =
  (playlist: Playlist): Thunk =>
  async dispatch => {
    try {
      const result = await api.getPlaylistParams(playlist)
      return dispatch({
        type: PLAYLIST_LOAD_PARAMS,
        playlist: result,
      })
    } catch (e) {
      dispatch(sendGenericError(e))
    }
  }
/**
 * Loads a playlist by its ID.
 * @param playlistId - ID of the playlist to load
 * @returns Thunk that loads playlist base object and maps to reducer objects
 */
export function loadPlaylist(playlistId: PlaylistId): Thunk {
  return async (dispatch, getState) => {
    dispatch({
      type: PLAYLIST_LOAD,
    })

    try {
      const response = await api.getPlaylistBaseObj(playlistId)
      mapResponseToReducerObjects(dispatch, getState, response)
      dispatch({
        type: PLAYLIST_LOAD_SUCCESS,
        playlistId,
        response,
      })
      return response
    } catch (error) {
      dispatch({
        type: PLAYLIST_LOAD_FAIL,
        playlistId,
        error,
      })
    }
  }
}
/**
 * Updates a playlist with the provided data.
 * @param playlistId - ID of the playlist to update
 * @param data - Partial playlist data to update
 * @returns Thunk that updates playlist and handles success/error states
 */
export function updatePlaylist(
  playlistId: PlaylistId,
  data: Partial<Playlist>,
): Thunk {
  return async dispatch => {
    try {
      dispatch({
        type: PLAYLIST_UPDATE,
        playlistId,
      })
      const response = await api.updatePlaylist(playlistId, data)
      dispatch({
        type: PLAYLIST_UPDATE_SUCCESS,
        playlistId,
        response,
      })
    } catch (error) {
      dispatch({
        type: PLAYLIST_UPDATE_FAIL,
        playlistId,
        error,
      })
    }
  }
}
/**
 * Updates multiple playlists with the same data.
 * @param playlistIds - Array of playlist IDs to update
 * @param data - Partial playlist data to apply to all playlists
 * @returns Thunk that batch updates playlists
 */
export function batchUpdatePlaylists(
  playlistIds: PlaylistId[],
  data: Partial<Playlist>,
): Thunk {
  return async dispatch => {
    try {
      dispatch({
        type: PLAYLISTS_UPDATE,
        playlistIds,
      })
      await api.batchUpdatePlaylists(playlistIds, data)
      dispatch({
        type: PLAYLISTS_UPDATE_SUCCESS,
        playlistIds,
        data,
      })
    } catch (error) {
      dispatch({
        type: PLAYLISTS_UPDATE_FAIL,
        playlistIds,
        error,
      })
    }
  }
}
/**
 * Gets the reducer key name based on playlist type.
 * @param playlistType - The type of playlist ('album', 'playlist', etc.)
 * @returns The corresponding reducer key string ('albums', 'playlists', or the original type)
 */
export function getKeyByPlaylistType(
  playlistType: Playlist['playlist_type'],
): string {
  switch (playlistType) {
    case 'album':
      return 'albums'

    case 'playlist':
      return 'playlists'

    default:
      return playlistType
  }
}
/**
 * Searches playlists by query and type with album feature support.
 * @param query - Search query string
 * @param playlistType - Type of playlist to search for
 * @returns Thunk that searches playlists and maps results to reducer objects
 */
export function searchPlaylists(
  query: string,
  playlistType: Playlist['playlist_type'],
): Thunk {
  return async (dispatch, getState) => {
    const key = getKeyByPlaylistType(playlistType)
    const albumsEnabled = isFeatureEnabled(getState(), {
      featureName: ALBUMS,
    })
    dispatch({
      type: PLAYLISTS_SEARCH,
      key,
    })

    try {
      const response = await api.searchPlaylistsWithTrackArtworks(
        query,
        albumsEnabled ? playlistType : undefined,
      )
      mapResponseToReducerObjects(dispatch, getState, {
        ...response,
        results: response.results.map(item => omit(item, ['is_featured'])),
      })
      dispatch({
        type: PLAYLISTS_SEARCH_SUCCESS,
        key,
        response,
      })
    } catch (error) {
      dispatch({
        type: PLAYLISTS_SEARCH_FAIL,
        key,
        error,
      })
    }
  }
}
/**
 * Loads the next page of playlists using pagination.
 * @param url - Pagination URL for the next page
 * @param playlistType - Type of playlist being paginated
 * @returns Thunk that loads next page of playlists
 */
export function loadNextPlaylists(
  url: string,
  playlistType: Playlist['playlist_type'],
): Thunk {
  return async (dispatch, getState) => {
    const key = getKeyByPlaylistType(playlistType)
    dispatch({
      type: PLAYLISTS_LOAD_NEXT,
      key,
    })

    try {
      const response = await api.getUrl<any>(
        url,
        null,
        'api.getPlaylistsLoadNext',
      )
      mapResponseToReducerObjects(dispatch, getState, {
        ...response,
        results: response.results.map(item => omit(item, ['is_featured'])),
      })
      dispatch({
        type: PLAYLISTS_LOAD_NEXT_SUCCESS,
        key,
        response,
      })
    } catch (error) {
      dispatch({
        type: PLAYLISTS_LOAD_NEXT_FAIL,
        key,
        error,
      })
    }
  }
}
/**
 * Updates multiple playlists in the store by merging new data with existing playlists.
 * @param playlists - Array of playlist objects to update
 * @returns Thunk that merges playlist data with existing state and dispatches update action
 */
export const updatePlaylists =
  (playlists: Playlist[]): ((dispatch: any, getState: GetState) => any) =>
  (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const state = getState()
    // update playlists without rewriting what's already in reducer but not present in update
    const updatedPlaylists = playlists.map(p => {
      const existingPlaylist = getPlaylist(state, {
        id: p.playlist_id,
      })
      return {
        ...(existingPlaylist ?? {}),
        ...p,
        tracks: p.tracks
          ? p.tracks.map(t => (t.id ? t.id : t))
          : existingPlaylist?.tracks ?? [],
      }
    })
    return dispatch({
      type: PLAYLIST_LOAD_PLAIN_UPDATE,
      playlists: updatedPlaylists,
    })
  }
/**
 * Loads curated playlists from the client library.
 * @returns Thunk that loads curated playlists
 */
export function loadCuratedPlaylists(): Thunk {
  return async dispatch => {
    dispatch({
      type: PLAYLISTS_CURATED_LOAD,
    })

    try {
      const { results } = await api.clientLibraryCuratedPlaylists()
      dispatch({
        type: PLAYLISTS_CURATED_LOAD_SUCCESS,
        results,
      })
    } catch (error) {
      dispatch({
        type: PLAYLISTS_CURATED_LOAD_FAIL,
        error,
      })
    }
  }
}
/**
 * Updates a playlist's base object and handles watermarking updates.
 * TODO: use the new wrapper for call api.updatePlaylistBaseObj
 * @param playlistId - ID of the playlist to update
 * @param update - Object containing the updates to apply
 * @param notificationMessage - Optional success/error notification messages
 * @returns Thunk that updates playlist base object and shows notifications
 */
export function updatePlaylistBaseObj(
  playlistId: PlaylistId,
  update: Record<string, any>,
  notificationMessage?: {
    success: string
    error: string
  },
): (dispatch: any) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any) => {
    try {
      const response = await api.updatePlaylistBaseObj(playlistId, update)
      // dispatch(loadBatch(result.versions))
      dispatch({
        type: WATERMARKING_UPDATE_ITEMS,
        items: response.versions,
      })
      if (notificationMessage?.success)
        pushUINotification({
          message: notificationMessage.success,
          level: 'success',
        })
    } catch (_error) {
      if (notificationMessage?.error)
        pushUINotification({
          message: notificationMessage.error,
          level: 'error',
        })
    }
  }
}
/**
 * Creates a partial playlist update action without making API calls.
 * @param playlistIds - Array of playlist IDs to update
 * @param data - Object containing the partial update data
 * @returns Action object for updating playlists with success type
 */
export const applyPartialPlaylistUpdate = (
  playlistIds: PlaylistId[],
  data: Record<string, any>,
): {
  data: any
  playlistIds: Array<PlaylistId>
  type: string
} => ({
  type: PLAYLISTS_UPDATE_SUCCESS,
  playlistIds,
  data,
})
/**
 * Loads playlist versions and optionally includes track data.
 * @param playlistId - ID of the playlist to load versions for
 * @param withTracks - Whether to include track data in the first version
 * @returns Thunk that loads playlist versions and returns the updated playlist from state
 */
export function loadPlaylistVersions(
  playlistId: PlaylistId,
  withTracks?: boolean,
): Thunk {
  return async (dispatch: any, getState: GetState) => {
    try {
      const response = await api.getPlaylistBaseObj(playlistId)
      const firstVersion = response.versions[0]

      if (withTracks) {
        const playlist = await api.getUrl<any>(
          firstVersion.url,
          null,
          'api.getPlaylistVersion',
        )
        dispatch(loadBatch([playlist]))
      } else {
        dispatch(loadBatch([firstVersion]))
      }

      return getState().playlists[playlistId]
    } catch (error) {
      captureError(error)
    }
  }
}
/**
 * Updates tags for a playlist with optimistic updates and error handling.
 * @param playlist - Playlist to update tags for
 * @param tags - Array of tag names to set
 * @returns Thunk that updates playlist tags with instant UI updates and rollback on error
 */
export function updatePlaylistTags(
  playlist: Playlist,
  tags: string[],
): (dispatch: any, getState: GetState) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    // pretend that altering tags never fail, so we can change tags instantly
    const updatedPlaylist = {
      ...playlist,
      tags,
      playlist_tags: tags.map((name: string) => {
        const tag: PlaylistTag | null | undefined = Object.values<PlaylistTag>(
          getState().entities.playlistTags,
        ).find((tag: PlaylistTag) => tag?.name === name)
        return {
          color: tag?.color,
          name: tag?.name,
        }
      }),
    }

    try {
      dispatch(updatePlaylists([updatedPlaylist]))
      await api.bulkUpdatePlaylistTags([playlist.playlist_id], tags)
    } catch (error) {
      // reverting state when error
      dispatch(updatePlaylists([playlist]))
      dispatch(sendGenericError(error))
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
    }
  }
}
